import {
  createEffect,
  onCleanup,
  untrack,
  Match,
  Switch,
  createMemo,
  onMount,
  createSignal,
  Show,
} from 'solid-js';
import { createMutable } from 'solid-js/store';
import { datadogRum } from '@datadog/browser-rum';
import { createQuery } from '@tanstack/solid-query';

import { createDeferred, Deferred } from '@dtvgo/utils';

import { PlayerDataContext } from '~/contexts/PlayerDataContext';

import {
  addLoadInterceptor,
  createPlayerData,
  PlayerState,
  broadcastCustomMessage,
  stopPlayback,
  bindPlayerData,
} from '~/cast/lib';
import { getPlaybackInfo } from '~/cast/VrioOtt';

import NpawMonitoring, { NpawMonitoringRef } from '~/components/NpawMonitoring';
import { MetadataUpdater } from '~/components/MetadataUpdater';

import IdleScreen from '~/screens/Idle';
import LaunchingScreen from '~/screens/Launching';
import ErrorScreen from '~/screens/Error';
import ControlsScreen from '~/screens/Controls';
import BufferingScreen from '~/screens/Buffering';
import LoadingScreen from '~/screens/Loading';

import {
  login,
  userId,
  datadogUser,
  tokensAreUsable,
  setCredentials,
  authError,
} from '~/store/credentials';
import { updateProfile } from '~/store/profile';
import { appConfigQuery } from '~/store/tanstack-query';

import { errorFactory } from '~/utils/errorFactory';
import {
  checkBlackout,
  checkParentalControl,
  getHeartbeatContent,
} from '~/utils/metadata';
import { CustomMessageType, NAMESPACE } from '~/utils/constants';
import { getErrorTexts } from '~/utils/messages';

import {
  CastUIState,
  createAssetChangeEffect,
  createErrorSignal,
  createGetStreamedTime,
  createHeartbeatController,
  createInitCastReceiverEffect,
  createInitPalSdkEffect,
  createUpdateErrorFactoryLocationEffect,
  useAutoplayNextEpisode,
  useGoLiveOnLongPause,
  useUiState,
} from './utils';

export function App() {
  let npawMonitoringRef: NpawMonitoringRef | undefined;
  let metadataFetchDeferred: Deferred<void> | undefined;
  const appConfig = createQuery(() => appConfigQuery());

  const [contentId, setContentId] = createSignal<
    { isLive: boolean; id: string; start?: number } | undefined
  >();

  const handleMetadataFetched = () => {
    if (metadataFetchDeferred) {
      metadataFetchDeferred.resolve();
      metadataFetchDeferred = undefined;
    }
  };

  const playerData = createMutable(createPlayerData());
  bindPlayerData(playerData);

  const playerState = () => playerData.state;
  const customData = createMemo(() => playerData.media?.customData);
  const isLive = () => customData()?.isLive ?? playerData?.isLive ?? false;
  const userOrigin = () =>
    customData()?.userOrigin ?? {
      msoProvider: '',
      playSource: '',
      userLogin: '',
    };
  const actionOrigin = () => customData()?.actionOrigin ?? '';
  const asset = () => customData()?.asset;
  const assetId = () => asset()?.vrioAssetId;
  const recoveryConfig = () => appConfig.data?.recovery;
  const progress = () => {
    if (playerData.isLive) {
      return playerData.currentTime - (playerData.liveSeekableRange?.end ?? 0);
    }

    return playerData.currentTime;
  };
  const duration = () => playerData.duration;

  const [error, handleError, isRecovering] = createErrorSignal(
    playerState,
    recoveryConfig,
  );

  const uiState = useUiState(playerState, error, isRecovering, authError);
  const streamedTime = createGetStreamedTime({ assetId, playerState });
  const streamedContentTime = createGetStreamedTime({ assetId, playerState });

  const { startHeartbeat, stopHeartbeat, programCompletion, updateProgress } =
    createHeartbeatController(() => appConfig.data?.heartbeat, {
      streamedContentTime,
      streamedTime,
      progress,
      duration,
      userOrigin,
      actionOrigin,
      isLive,
      asset,
    });

  const { onAssetReceived, onFinished } = useAutoplayNextEpisode();

  const onLoadRequest = async (request: {
    idToken: string;
    profileId: string;
    isLive: boolean;
    contentId: string;
    start?: number;
  }) => {
    await login(request.idToken);
    await updateProfile(request.profileId);

    setContentId({
      isLive: request.isLive,
      id: request.contentId,
      start: request.start,
    });

    if (request.isLive) {
      metadataFetchDeferred = createDeferred();
      return metadataFetchDeferred.promise;
    }

    return undefined;
  };

  const onPlaybackInfoAvailable = (content: {
    isLive: boolean;
    contentId: string;
  }) => {
    const currentAsset = untrack(asset);

    startHeartbeat({
      playbackId: content.contentId,
      isLive: content.isLive,
      ...(currentAsset && getHeartbeatContent(currentAsset)),
    });

    if (!currentAsset) return;

    if (content.isLive && 'live' in currentAsset) checkBlackout(currentAsset);

    if (appConfig.data?.parentalControl) checkParentalControl(currentAsset);

    broadcastCustomMessage(NAMESPACE, {
      type: CustomMessageType.ASSET_LOAD,
      isLive: content.isLive,
      asset: currentAsset,
    });

    onAssetReceived(currentAsset, content.isLive);
  };

  useGoLiveOnLongPause(
    () => appConfig.data!.goToLiveLongPauseMinutes,
    () => isLive() && customData()?.start === undefined,
  );

  createInitCastReceiverEffect(
    () => appConfig.data && { shakaVersion: appConfig.data.shakaVersion },
    { onShutdown: stopHeartbeat },
  );

  createInitPalSdkEffect(() => appConfig.data && { allowStorage: true });

  onMount(() => {
    tokensAreUsable()
      .then((usable) => !usable && setCredentials(undefined))
      .catch(() => setCredentials(undefined));
  });

  createEffect(() => {
    if (playerData) {
      let newProgress = playerData.currentTime;

      if (playerData.isLive) {
        newProgress =
          playerData.currentTime - (playerData.liveSeekableRange?.end ?? 0);
      }

      updateProgress(newProgress);
    }
  });

  createEffect(() => {
    if (appConfig.data) {
      const cleanup = addLoadInterceptor(
        getPlaybackInfo(appConfig.data, {
          onLoadRequest,
          onPlaybackInfoAvailable,
          onAuthorizerError: (authorizerError) => {
            const vrioOttError =
              errorFactory.ensureVrioOttError(authorizerError);
            npawMonitoringRef?.reportError(
              vrioOttError,
              false,
              'onAuthorizerError',
            );
            handleError(vrioOttError);
          },
          onFinished: () => {
            setContentId(undefined);
            onFinished();
            stopHeartbeat();
          },
        }),
      );

      onCleanup(cleanup);
    }
  });

  createAssetChangeEffect(assetId, () => {
    const currentAsset = untrack(asset);
    if (currentAsset) {
      try {
        if ('live' in currentAsset) checkBlackout(currentAsset);
        if (untrack(() => appConfig.data?.parentalControl))
          checkParentalControl(currentAsset);

        broadcastCustomMessage(NAMESPACE, {
          type: CustomMessageType.ASSET_LOAD,
          isLive: true,
          asset: currentAsset,
        });
      } catch (assetError) {
        const vrioOttError = errorFactory.ensureVrioOttError(assetError);
        npawMonitoringRef?.reportError(vrioOttError, true, 'assetChangeEffect');
        handleError(vrioOttError);
        stopPlayback();
      }
    }
  });

  createUpdateErrorFactoryLocationEffect(uiState);

  // Report the error to senders
  createEffect(() => {
    if (uiState() === CastUIState.ERROR) {
      const currentError = untrack(error);
      if (currentError) {
        const { title, message } = getErrorTexts(currentError);
        broadcastCustomMessage(NAMESPACE, {
          type: CustomMessageType.ERROR,
          error_data: {
            error_location: currentError.location,
            error_type: currentError.code,
            error_source: currentError.source,
            error_complement_code: currentError.externCode,
            error_vms: untrack(
              () => playerData.media?.customData?.asset?.vmsId,
            ),
            error_customer: untrack(userId),
            error_title: title,
            error_subtitle: message,
          },
        });
      }
    }
  });

  // When the system became ready, notify senders
  createEffect((prevState?: typeof playerData.state) => {
    if (
      prevState === PlayerState.LAUNCHING &&
      playerData.state === PlayerState.IDLE
    ) {
      broadcastCustomMessage(NAMESPACE, {
        type: CustomMessageType.CONNECTED,
        authorized_connection: true,
      });
    }
    return playerData.state;
  });

  createEffect(() => {
    const userData = datadogUser();
    if (userData) {
      datadogRum.setUser(userData);
    } else {
      datadogRum.clearUser();
    }
  });

  return (
    <PlayerDataContext.Provider value={playerData}>
      <NpawMonitoring ref={npawMonitoringRef} />
      <Show when={contentId()} keyed>
        {(currentContentId) => (
          <MetadataUpdater
            contentId={currentContentId}
            streamedContentTime={streamedContentTime}
            asset={{
              mainAssetId: customData()?.mainAssetId ?? '',
              vmsId: customData()?.vmsId ?? '',
              type: customData()?.assetType ?? '',
            }}
            onFinished={handleMetadataFetched}
            onProgramCompletion={programCompletion}
          />
        )}
      </Show>
      <Switch>
        <Match when={uiState() === CastUIState.LAUNCHING}>
          <LaunchingScreen />
        </Match>
        <Match when={uiState() === CastUIState.ERROR}>
          <ErrorScreen error={authError() ?? error()!} />
        </Match>
        <Match when={uiState() === CastUIState.IDLE}>
          <IdleScreen />
        </Match>
        <Match when={uiState() === CastUIState.LOADING}>
          <LoadingScreen />
        </Match>
        <Match when={uiState() === CastUIState.BUFFERING}>
          <BufferingScreen />
        </Match>
        <Match when={uiState() === CastUIState.CONTROLS}>
          <ControlsScreen />
        </Match>
      </Switch>
    </PlayerDataContext.Provider>
  );
}
